import { Badge, Button, Card, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Check, Inbox, X } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { getForm, listResponses, type FormField, type FormSubmission } from '../../lib/forms-api'

function renderAnswer(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.map(String).join(', ')
  return String(value)
}

export function FormResponses() {
  const params = useParams<{ id: string }>()
  const formId = params.id!

  const formQuery = useQuery({
    queryKey: ['form', formId],
    queryFn: () => getForm(formId),
  })

  const responsesQuery = useQuery({
    queryKey: ['form-responses', formId],
    queryFn: () => listResponses(formId),
  })

  // Stable column order: every non-consent field, in builder order.
  const columns = useMemo<FormField[]>(
    () => (formQuery.data?.fields ?? []).filter((f) => f.type !== 'consent'),
    [formQuery.data],
  )

  const rows = responsesQuery.data?.items ?? []

  return (
    <div className="space-y-6">
      <PageHeader
        title={formQuery.data ? `${formQuery.data.name} — Responses` : 'Responses'}
        description={
          responsesQuery.data
            ? `${responsesQuery.data.total} total submission${responsesQuery.data.total === 1 ? '' : 's'}.`
            : 'Submissions collected for this form.'
        }
        actions={
          <Button asChild variant="ghost">
            <Link to="/app/forms">
              <ArrowLeft />
              Back
            </Link>
          </Button>
        }
      />

      <Card className="overflow-hidden p-0">
        {responsesQuery.isLoading || formQuery.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading responses…
          </div>
        ) : responsesQuery.isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load responses.</p>
            <p className="text-xs text-dim">
              {responsesQuery.error instanceof Error
                ? responsesQuery.error.message
                : 'Please try again.'}
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="space-y-3 py-20 text-center">
            <Inbox className="mx-auto size-8 text-dim" aria-hidden="true" />
            <p className="text-sm text-muted">No responses yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Submitted</th>
                  {columns.map((c) => (
                    <th key={c.id} className="px-5 py-3 font-semibold">
                      {c.label}
                    </th>
                  ))}
                  <th className="px-5 py-3 font-semibold">Consent</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: FormSubmission) => (
                  <tr
                    key={r.id}
                    className="border-b border-border-soft align-top transition-colors last:border-0 hover:bg-card-hover"
                  >
                    <td className="whitespace-nowrap px-5 py-3 text-muted">
                      {new Date(r.submittedAt).toLocaleString()}
                    </td>
                    {columns.map((c) => (
                      <td key={c.id} className="px-5 py-3 text-fg">
                        {renderAnswer(r.answers[c.key])}
                      </td>
                    ))}
                    <td className="px-5 py-3">
                      {r.consentGiven ? (
                        <Check className="size-4 text-success" aria-label="Consent given" />
                      ) : (
                        <X className="size-4 text-dim" aria-label="No consent" />
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant="neutral" className="capitalize">
                        {r.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
